/**
 * The "Our History" section of the two About Us pages: a large two-word title and a year with a
 * sub-title, a photo, a lead paragraph, a paragraph, and an optional button.
 *
 * Used by `src/pages/about-us/index.tsx` (Ryerson, copy `src/content/ryerson/about-us/index.ts` →
 * `OurHistory`, button to `/about-us/history/`) and `src/pages/who-we-are/index.tsx` (Southern
 * Tool Steel, copy `src/content/sts/who-we-are/index.ts` → `WhoWeAreOurHistory`, no button). Both
 * pages place it in `components/Shared/AllSections/AllSections.tsx` with the section title
 * `title1 + " " + title2`; `AllSections` slugifies that title into the section's anchor id.
 *
 * From `lg` up the title and year slide in with `react-scroll-parallax` as the page scrolls, with
 * one set of sizes and offsets between `lg` and `xl` and another from `xl` up. Below `lg` the
 * layout is a plain stack with no parallax.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Media } from "@ryerson/frontend.next";
import { ContentSection } from "@ryerson/frontend.layout";
import { Grid, Column, Row } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { ButtonLink } from "@ryerson/frontend.navigation";
import { ParallaxProvider, Parallax } from "react-scroll-parallax";
import Spacer from "@components/Shared/Spacer/Spacer";

/** One language's copy for the section. */
export type OurHistoryContent = {
	/**
	 * First title word, in the default text colour. Neither title word is drawn below `lg`, where
	 * the `AllSections` accordion tab header shows the section title instead.
	 */
	title1: string;
	/**
	 * Second title word, drawn in the theme's header colour. May be empty (Ryerson `fr` and
	 * `es`).
	 */
	title2: string;
	/** The large year ("1842", "1968"). */
	years: string;
	/**
	 * Text beside the year. From `lg` up its container keeps newlines (`white-space: pre-line`);
	 * the mobile container doesn't set that.
	 */
	subTitle: string;
	/** Lead paragraph above `description`; also the photo's `alt` text. */
	superDescription: string;
	description: string;
	/** Not rendered. */
	videoOverlay: string;
	/** The photo shown in the section, as an `<img>`. Despite the name, no video is drawn. */
	videoThumbnail: string;
	/**
	 * Not rendered. The section once played this video and now shows `videoThumbnail` as a still
	 * photo; the Ryerson copy still sets it.
	 */
	videoId?: string;
	/** Target of the button; no button is drawn when it is absent. */
	actionUrl?: string;
	/** Button label; an array is passed to `ButtonLink` as separate lines. */
	actionLabel: string | string[];
};

/** Props for {@link OurHistory}. */
export type OurHistoryProps = {
	content: OurHistoryContent;
};

const SubtitleTextContainer = styled.div`
	display: inline-block;
	white-space: pre-line;
	vertical-align: middle;
	margin-right: 20px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.sm}) {
				padding-bottom: 35px;
			}
		`;
	}}
`;

const SecondarySubtitleTextContainer = styled.div`
	display: inline-block;
	width: 60%;
	vertical-align: middle;
`;

const TitleText = styled.div`
	position: absolute;
	left: 0px;
	width: 100%;
	white-space: nowrap;
	max-width: 100%;
	margin-top: -60px;
`;

const SubtitleText = styled.div`
	position: absolute;
	left: 0;
	width: 50%;
	max-width: 50%;
	white-space: pre-line;
	margin-top: 30%;
`;

const TextContainer = styled.div`
	margin-left: 120px;
	width: 62%;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (min-width: ${theme.breakpoints.lg}) and (max-width: ${theme
					.breakpoints.xl}) {
				margin-left: 0px;
			}
		`;
	}}
`;

const SuperDescriptionTextContainer = styled.div`
	margin-top: 92px;
	margin-bottom: 85px;
`;

const DescriptionTextContainer = styled.div`
	margin-bottom: 5px;
`;

/** Renders the section: the parallax layout from `lg` up, a plain stack below. */
const OurHistory: React.FC<OurHistoryProps> = ({ content }) => {
	const { theme } = useTheme();

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ParallaxProvider>
					<ContentSection type="secondary" vPadding="190px" hPadding="0">
						<Media lessThan="xl">
							{/* iPad Pro */}
							<Parallax translateX={[-15, 0]}>
								<TitleText>
									<Typography
										variant="div"
										css={{
											fontSize: "180px",
											fontFamily: theme.typography.fontFamilies.headings,
											letterSpacing: "-0.06em",
										}}
									>
										{content.title1}{" "}
										<Typography
											font="inherit"
											color={theme.colors.primary.header}
										>
											{content.title2}
										</Typography>
									</Typography>
								</TitleText>
							</Parallax>
							<Parallax translateY={["200px", "10px"]}>
								<SubtitleText>
									<Typography
										variant="div"
										css={{
											fontSize: "180px",
											color: theme.colors.primary.header,
											fontFamily: theme.typography.fontFamilies.headings,
											letterSpacing: "-0.06em",
											marginTop: "-40px",
										}}
									>
										{content.years}
									</Typography>
									<Typography
										variant="h2"
										css={{
											paddingLeft: "20px",
											fontFamily: theme.typography.fontFamilies.headings,
											letterSpacing: "-0.04em",
											margin: "0",
											marginTop: "-75px",
										}}
										color={theme.colors.primary.gray}
									>
										{content.subTitle}
									</Typography>
								</SubtitleText>
							</Parallax>
						</Media>
						<Media greaterThan="lg">
							{/* Desktop */}
							<Parallax translateX={[-75, -15]}>
								<TitleText>
									<Typography
										variant="div"
										css={{
											fontSize: "210px",
											fontFamily: theme.typography.fontFamilies.headings,
											letterSpacing: "-0.06em",
										}}
									>
										{content.title1}{" "}
										<Typography
											font="inherit"
											color={theme.colors.primary.header}
										>
											{content.title2}
										</Typography>
									</Typography>
								</TitleText>
							</Parallax>
							<Parallax translateY={["200px", "0px"]}>
								<SubtitleText>
									<Typography
										variant="div"
										css={{
											fontSize: "210px",
											color: theme.colors.primary.header,
											fontFamily: theme.typography.fontFamilies.headings,
											letterSpacing: "-0.06em",
										}}
									>
										{content.years}
									</Typography>
									<Typography
										variant="h2"
										css={{
											paddingLeft: "20px",
											fontFamily: theme.typography.fontFamilies.headings,
											letterSpacing: "-0.04em",
											margin: "0",
											marginTop: "-75px",
										}}
										color={theme.colors.primary.gray}
									>
										{content.subTitle}
									</Typography>
								</SubtitleText>
							</Parallax>
						</Media>
						<Grid>
							<Row>
								<Column lg={5}>{""}</Column>
								<Column lg={7}>
									<Spacer css={css`height: 150px;`}/>
									<img
										src={content.videoThumbnail}
										alt={content.superDescription}
									/>
									<TextContainer theme={theme}>
										<SuperDescriptionTextContainer>
											<Typography variant="p" type="secondary" size="lg">
												{content.superDescription}
											</Typography>
										</SuperDescriptionTextContainer>
										<DescriptionTextContainer>
											<Typography variant="p" type="secondary" size="md">
												{content.description}
											</Typography>
										</DescriptionTextContainer>
										{content.actionUrl ? (
											<ButtonLink
												label={content.actionLabel}
												to={content.actionUrl}
												type="secondary"
											/>
										) : (
											<></>
										)}
									</TextContainer>
								</Column>
							</Row>
						</Grid>
						<Spacer />
					</ContentSection>
				</ParallaxProvider>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="primary" hPadding="0px" vPadding="40px">
					<SubtitleTextContainer theme={theme}>
						<Typography variant="h1">{content.years}</Typography>
					</SubtitleTextContainer>
					<SecondarySubtitleTextContainer 
						css={css`@media (max-width: ${theme.breakpoints.sm}){
							padding-bottom: 35px;
						}`}>
						<Typography variant="p" size="md">
							{content.subTitle}
						</Typography>
					</SecondarySubtitleTextContainer>
					<img
						src={content.videoThumbnail}
						alt={content.superDescription}
						css={css`
							width: 100%;
							height: 200px;
						`}
					/>
					<Typography variant="p" size="xl" color={theme.colors.primary.gray}>
						{content.superDescription}
					</Typography>
					<Typography variant="p" size="md">
						{content.description}
					</Typography>
					{content.actionUrl ? (
						<>
							<Spacer
								css={css`
									height: 21px;
								`}
							/>
							<ButtonLink
								label={content.actionLabel}
								to={content.actionUrl}
								type="primary"
							/>
							<Spacer
								css={css`
									height: 21px;
								`}
							/>
						</>
					) : (
						<></>
					)}
				</ContentSection>
			</Media>
		</>
	);
};

export default OurHistory;
